import React from 'react';

interface BarData {
  label: string;
  value2024: number;
  value2027: number;
  backgroundColor?: string;
  group: 'US' | 'China' | 'World';
}

export default function ComputeBreakdown() {
  // Chart dimensions
  const width = 900;
  const height = 700;
  const margin = { top: 60, right: 15, bottom: 40, left: 180 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  
  // Data for the bars - the total for 2024 should be 8.5M
  // Actual H100-equivalents in millions
  const data: BarData[] = [
    { 
      label: "OpenBrain", 
      value2024: 0.6, // Part of the 8.5M total
      value2027: 20,
      group: 'US'
    },
    { 
      label: "US 2nd in AGI\ndevelopment", 
      value2024: 0.54, 
      value2027: 16,
      group: 'US'
    },
    { 
      label: "US 3rd in AGI\ndevelopment", 
      value2024: 0.42, 
      value2027: 14,
      group: 'US'
    },
    { 
      label: "US 4th in AGI\ndevelopment", 
      value2024: 0.4, 
      value2027: 9,
      group: 'US'
    },
    { 
      label: "Rest of US and\nnon-AGI-development", 
      value2024: 5.0, 
      value2027: 20,
      group: 'US'
    },
    { 
      label: "DeepCent", 
      value2024: 0.1, 
      value2027: 10,
      group: 'China'
    },
    { 
      label: "Rest of China and\nnon-AGI-development", 
      value2024: 0.12, 
      value2027: 2,
      group: 'China'
    },
    { 
      label: "Rest of World", 
      value2024: 1.32, 
      value2027: 9,
      group: 'World'
    }
  ];
  
  // Verify total for 2024 is close to 8.5M
  const total2024 = data.reduce((sum, d) => sum + d.value2024, 0);
  console.log("Total 2024:", total2024); // Should be close to 8.5
  
  // Find maximum value for scaling
  const maxValue = Math.max(
    ...data.map(d => Math.max(d.value2024 * 3, d.value2027))
  );
  
  // Round up to nearest 5M for nice axis
  const axisMax = 25; // Fixed to 25M as in the original
  
  // X-axis ticks
  const xTicks = [0, 5, 10, 15, 20, 25];
  
  // Bar height and spacing
  const barHeight = 18;
  const barSpacing = chartHeight / data.length;
  
  // Scale for x-axis
  const xScale = (value: number) => (value / axisMax) * chartWidth;
  
  // Get background color based on group
  const getBackgroundColor = (group: string) => {
    switch(group) {
      case 'US':
        return "rgba(173, 220, 173, 0.05)";
      case 'China':
        return "rgba(220, 137, 137, 0.05)";
      default:
        return "rgba(201, 202, 200, 0.05)";
    }
  };
  
  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={30}
          fontSize="28"
          fontFamily="monospace"
          className="text-[28px] font-mono md:text-[24px] sm:text-[20px]"
          fill="#333333"
        >
          Compute breakdown by AI companies
        </text>
        
        {/* Subtitle */}
        <text
          x={margin.left}
          y={55}
          fontSize="16"
          fontFamily="monospace"
          className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
          fill="#666666"
        >
          2024 estimate and 2027 projection, H100-equivalents
        </text>
        
        {/* Background Stripes for Rows */}
        {data.map((d, i) => (
          <rect
            key={`bg-${i}`}
            x={margin.left}
            y={margin.top + i * barSpacing}
            width={chartWidth}
            height={barSpacing}
            fill={getBackgroundColor(d.group)}
            stroke="#e0e0e0"
            strokeWidth="1"
          />
        ))}
        {/* Horizontal Highlight Lines for Top and Bottom */}
        <line
          x1={margin.left}
          y1={margin.top}
          x2={margin.left + chartWidth}
          y2={margin.top}
          stroke="#333333"
          strokeWidth="2"
        />
        <line
          x1={margin.left}
          y1={margin.top + chartHeight}
          x2={margin.left + chartWidth}
          y2={margin.top + chartHeight}
          stroke="#333333"
          strokeWidth="2"
        />

        {/* Border around China group (rows 6 & 7; indexes 5 & 6) */}
        <line
        x1={margin.left}
        y1={margin.top + 5 * barSpacing}
        x2={margin.left + chartWidth}
        y2={margin.top + 5 * barSpacing}
        stroke="#333333"
        strokeWidth="2"
      />
      <line
        x1={margin.left}
        y1={margin.top + 7 * barSpacing}
        x2={margin.left + chartWidth}
        y2={margin.top + 7 * barSpacing}
        stroke="#333333"
        strokeWidth="2"
      />
        {/* X-axis and gridlines */}
        {xTicks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line
              x1={margin.left + xScale(tick)}
              y1={margin.top}
              x2={margin.left + xScale(tick)}
              y2={margin.top + chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
            />
            <text
              x={margin.left + xScale(tick)}
              y={margin.top + chartHeight + 20}
              textAnchor="middle"
              fontSize={14}
              fontFamily="monospace"
              fill="#666666"
            >
              {tick}M
            </text>
          </g>
        ))}
        
        {/* Y-axis labels (Company names) */}
        {data.map((d, i) => {
          // Split multi-line labels
          const lines = d.label.split('\n');
          
          return (
            <g key={`label-${i}`}>
              {lines.map((line, lineIndex) => (
                <text
                  key={`label-${i}-line-${lineIndex}`}
                  x={margin.left - 10}
                  y={margin.top + i * barSpacing + barSpacing / 2 + (lineIndex - lines.length / 2 + 0.5) * 18}
                  textAnchor="end"
                  fontSize={14}
                  fontFamily="monospace"
                  fill="#333333"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
        
        {/* 2024 Bars */}
        {data.map((d, i) => (
          <g key={`bar2024-${i}`}>
            <rect
              x={margin.left}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 2) / 2}
              width={xScale(d.value2024)}
              height={barHeight}
              fill="#333333"
              stroke="#ffffff"
              strokeWidth="1"
            />
            <text
            x={margin.left + xScale(d.value2024) + 5}
            y={margin.top + i * barSpacing + (barSpacing - barHeight) / 2 + barHeight / 2 - 4}
            textAnchor="start"
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
            dominantBaseline="bottom"
            >
            {(d.value2024 / 8.5 * 100).toFixed(1)}%
            </text>
          </g>
        ))}
        
        {/* 2027 Bars */}
        {data.map((d, i) => {
        const isChina = d.group === "China";
        const barY = margin.top + i * barSpacing + (barSpacing - barHeight * 2) / 2 + barHeight;
        const barWidth = xScale(d.value2027);
        const fillColor = isChina ? "rgb(127, 43, 43)" : "var(--accent, #006000)";

        return (
          <g key={`bar2027-${i}`}>
            <rect
              x={margin.left}
              y={barY}
              width={barWidth}
              height={barHeight}
              fill={fillColor}
              stroke="#ffffff"
              strokeWidth="1"
            />
            <text
              x={margin.left + barWidth + 5}
              y={barY + barHeight / 2}
              textAnchor="start"
              fontSize={14}
              fontFamily="monospace"
              fill="#333333"
              dominantBaseline="middle"
            >
              {d.value2027}%
            </text>
          </g>
        );
      })}
{/* Legend */}
        
<g transform={`translate(${width - margin.right - 235}, 605)`}>
        {/* 2024 Legend Item */}
        <rect
            x={0}
            y={0}
            width={15}
            height={15}
            fill="#333333"
        />
        <text
            x={20}
            y={11.5}
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
        >
            December 2024 estimate
        </text>
        
        {/* 2027 Legend Item */}
        <rect
            x={0}
            y={25} // Moved down by 25 pixels
            width={15}
            height={15}
            fill="var(--accent, #006000)"
        />
        <polygon 
            points="0,40 15,40 15,25" 
            fill="rgb(127, 43, 43)"
            stroke="white" 
            strokeWidth=".4" 
        />
        <text
            x={20} // Changed from 220 to 20
            y={37.5} // Changed from 12 to 37 (25 pixels down from the first item)
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
        >
            December 2027 projection
        </text>
        </g>
      </svg>
    </div>
  );
}